// services/connectionService.js
//
// Student connections ("Connect"). Docs live in `connections/{pairId}`
// (pairId = [uidA, uidB].sort().join("_")); clients only READ them, every
// write goes through callables. Club-mate search reads club_members and the
// server-written `user_public` mirror. Returns { data, error }; never throws.
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions, COLLECTIONS } from "../config/firebase";
import { chunk, FIRESTORE_IN_LIMIT } from "../utils/arrays";
import { fetchDocsByIds } from "./firestoreBatch";

const sendRequestCallable = httpsCallable(functions, "sendConnectionRequest");
const respondCallable = httpsCallable(functions, "respondToConnection");
const removeCallable = httpsCallable(functions, "removeConnection");

function errorCodeOf(error) {
  return String((error && error.code) || "").replace(/^functions\//, "");
}

function friendlyError(error, fallback) {
  const code = errorCodeOf(error);
  if (code === "permission-denied") return "You can't connect with this person.";
  if (code === "not-found") return "That request is no longer available.";
  if (code === "unauthenticated") return "You must be signed in.";
  return fallback;
}

// Normalise a connection doc relative to the signed-in user.
function shape(snap, me) {
  const d = snap.data() || {};
  const uids = d.participant_uids || [];
  const otherUid = uids.find((u) => u !== me) || null;
  let state = "none";
  if (d.status === "accepted") state = "connected";
  else if (d.status === "pending") state = d.requester_uid === me ? "requested" : "incoming";
  return { id: snap.id, ...d, otherUid, state };
}

export class ConnectionService {
  static pairId(uidA, uidB) {
    return [uidA, uidB].sort().join("_");
  }

  // Everyone who shares at least one of `clubIds` with me (excluding me):
  // club_members (club_id `in` chunks) -> unique user_ids -> user_public docs
  // (documentId `in` chunks). data: students[] (unsorted; the screen sorts).
  static async searchClubmates({ clubIds = [] } = {}) {
    try {
      const me = auth.currentUser?.uid;
      const ids = [...new Set((clubIds || []).filter(Boolean))];
      if (!me || !ids.length) return { data: [], error: null };
      const memberSnaps = await Promise.all(
        chunk(ids, FIRESTORE_IN_LIMIT).map((c) =>
          getDocs(query(collection(db, COLLECTIONS.CLUB_MEMBERS), where("club_id", "in", c)))
        )
      );
      const uids = new Set();
      memberSnaps.forEach((snap) =>
        snap.docs.forEach((d) => {
          const uid = d.data().user_id;
          if (uid && uid !== me) uids.add(uid);
        })
      );
      if (!uids.size) return { data: [], error: null };
      const byId = await fetchDocsByIds(COLLECTIONS.USER_PUBLIC, [...uids], { strict: true });
      const students = [...byId].map(([id, data]) => ({ id, ...data }));
      return { data: students, error: null };
    } catch (error) {
      if (__DEV__) console.error("searchClubmates error:", error);
      return { data: null, error: "Couldn't load people in your clubs. Please try again." };
    }
  }

  // Live list of my pending + accepted connections (shaped, see `shape`).
  // `onError` (optional): called instead of onChange([]) on a listener error,
  // for callers that must not read a failure as "no connections".
  static subscribeMyConnections(onChange, onError) {
    const me = auth.currentUser?.uid;
    if (!me) {
      onChange([]);
      return () => {};
    }
    const q = query(
      collection(db, COLLECTIONS.CONNECTIONS),
      where("participant_uids", "array-contains", me)
    );
    return onSnapshot(
      q,
      (snapshot) => onChange(snapshot.docs.map((d) => shape(d, me))),
      (error) => {
        if (__DEV__) console.error("subscribeMyConnections error:", error);
        if (onError) onError(error);
        else onChange([]);
      }
    );
  }

  // Live status of one pair. A missing doc (or a rules denial on a missing
  // doc) is reported as { state: "none" }.
  // onError (optional): a listener error is reported there instead of as "no
  // connection", so a caller can keep its last known state.
  static subscribeConnection(otherUid, onChange, onError) {
    const me = auth.currentUser?.uid;
    if (!me || !otherUid || me === otherUid) {
      onChange(null);
      return () => {};
    }
    return onSnapshot(
      doc(db, COLLECTIONS.CONNECTIONS, ConnectionService.pairId(me, otherUid)),
      (snap) => onChange(snap.exists() ? shape(snap, me) : null),
      (error) => (onError ? onError(error) : onChange(null))
    );
  }

  // One-shot status via the deterministic pair doc. data: connection | null.
  // A missing doc is "no connection" (rules deny reads of non-existent pair
  // docs, so any error is treated the same way).
  static async getConnectionStatus(otherUid) {
    try {
      const me = auth.currentUser?.uid;
      if (!me || !otherUid || me === otherUid) return { data: null, error: null };
      const snap = await getDoc(doc(db, COLLECTIONS.CONNECTIONS, ConnectionService.pairId(me, otherUid)));
      return { data: snap.exists() ? shape(snap, me) : null, error: null };
    } catch {
      return { data: null, error: null };
    }
  }

  // uid -> user_public doc ({ id, full_name, major, graduation_year,
  // avatar_url }). Missing / unreadable profiles are simply absent.
  static async getPublicProfiles(uids) {
    const out = new Map();
    await Promise.all(
      [...new Set(uids || [])].map(async (uid) => {
        try {
          const snap = await getDoc(doc(db, COLLECTIONS.USER_PUBLIC, uid));
          if (snap.exists()) out.set(uid, { id: uid, ...snap.data() });
        } catch (error) {
          if (__DEV__) console.error("getPublicProfiles error:", error);
        }
      })
    );
    return out;
  }

  // `note` (optional) rides along only when non-empty after trim; the server
  // re-trims and caps it at CONNECTION_LIMITS.note.
  static async sendConnectionRequest(targetUid, note) {
    try {
      if (!auth.currentUser) return { data: null, error: "You must be signed in." };
      if (!targetUid) return { data: null, error: "Missing user." };
      const trimmed = typeof note === "string" ? note.trim() : "";
      const res = await sendRequestCallable(trimmed ? { targetUid, note: trimmed } : { targetUid });
      return { data: res.data, error: null };
    } catch (error) {
      if (__DEV__) console.error("sendConnectionRequest error:", error);
      return { data: null, error: friendlyError(error, "Couldn't send the request. Please try again.") };
    }
  }

  static async respond(connectionId, accept) {
    try {
      const res = await respondCallable({ connectionId, accept: !!accept });
      return { data: res.data, error: null };
    } catch (error) {
      if (__DEV__) console.error("respondToConnection error:", error);
      return {
        data: null,
        error: friendlyError(error, "Something went wrong. Please try again."),
        code: errorCodeOf(error),
      };
    }
  }

  static async remove(connectionId) {
    try {
      const res = await removeCallable({ connectionId });
      return { data: res.data, error: null };
    } catch (error) {
      if (__DEV__) console.error("removeConnection error:", error);
      return { data: null, error: friendlyError(error, "Couldn't remove the connection. Please try again.") };
    }
  }
}
